import React from 'react';
import { BrowserRouter as Router, Routes, Route, Link as RouterLink, useLocation } from 'react-router-dom';
import {
  Box,
  VStack,
  HStack,
  Text,
  Container,
  Image,
  Flex,
  Spacer,
  Link,
} from '@chakra-ui/react';
import Home from './pages/Home';
import FAQ from './pages/FAQ';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Terms from './pages/Terms';
import RefundPolicy from './pages/RefundPolicy';
import Profile from './pages/Profile';
import Pricing from './pages/Pricing';
import PricingStatic from './pages/PricingStatic';
import ScrollToTop from './components/ScrollToTop';

// Header Component
const Header = () => {
  const location = useLocation();
  const linkColor = 'gray.600';
  const activeLinkColor = '#135200';

  return (
    <Box
      as="header"
      bg="white"
      borderBottom="1px"
      borderColor="gray.200"
      position="sticky"
      top={0}
      zIndex={10}
    >
      <Container maxW="container.xl">
        <Flex h={16} alignItems="center">
          <Link as={RouterLink} to="/" _hover={{ textDecoration: 'none' }} display="flex" alignItems="center" gap={2.5} minH="44px">
            <Image
              src="/images/logo.png"
              alt="Email Cleaner Logo"
              h={10}
              fallback={
                <Box
                  w={10}
                  h={10}
                  bg="#52c41a"
                  borderRadius="md"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  color="#1A1A1A"
                  fontWeight="bold"
                  fontSize="sm"
                >
                  EC
                </Box>
              }
            />
            <Box lineHeight="1.1">
              <Text fontSize="lg" fontWeight="bold" color="gray.900">
                Email Cleaner
              </Text>
              <Text fontSize="xs" fontWeight="medium" color="gray.500">
                Clean your Gmail inbox
              </Text>
            </Box>
          </Link>

          <Spacer />

          {/* Navigation Links */}
          <Box as="nav" aria-label="Main navigation">
          <HStack spacing={6} mr={6}>
            <Link
              as={RouterLink}
              to="/"
              color={location.pathname === '/' ? activeLinkColor : linkColor}
              fontWeight={location.pathname === '/' ? 'semibold' : 'medium'}
              _hover={{ color: activeLinkColor, textDecoration: 'none' }}
              transition="color 0.2s"
              py={2}
            >
              Home
            </Link>
            <Link
              as={RouterLink}
              to="/pricing"
              color={location.pathname === '/pricing' ? activeLinkColor : linkColor}
              fontWeight={location.pathname === '/pricing' ? 'semibold' : 'medium'}
              _hover={{ color: activeLinkColor, textDecoration: 'none' }}
              transition="color 0.2s"
              py={2}
            >
              Pricing
            </Link>
            <Link
              as={RouterLink}
              to="/faq"
              color={location.pathname === '/faq' ? activeLinkColor : linkColor}
              fontWeight={location.pathname === '/faq' ? 'semibold' : 'medium'}
              _hover={{ color: activeLinkColor, textDecoration: 'none' }}
              transition="color 0.2s"
              py={2}
            >
              FAQ
            </Link>
          </HStack>
          </Box>
        </Flex>
      </Container>
    </Box>
  );
};

// Layout Component (Header + Footer wrapper)
const Layout = ({ children }) => {
  return (
    <Flex direction="column" minH="100vh">
      <Header />
      <Box flex="1">
        {children}
      </Box>
      <Footer />
    </Flex>
  );
};

// Footer Component
const Footer = () => {
  const location = useLocation();
  const footerBg = 'gray.100';
  const footerBorder = 'gray.200';
  const footerText = 'gray.600';
  const linkColor = '#135200';
  const activeLinkColor = '#135200';

  return (
    <Box
      as="footer"
      bg={footerBg}
      borderTop="1px"
      borderColor={footerBorder}
    >
      <Container maxW="container.xl" py={8}>
        <VStack spacing={6}>
          {/* Footer Navigation Links */}
          <HStack spacing={8} flexWrap="wrap" justify="center">
            <Link
              as={RouterLink}
              to="/privacy-policy"
              color={location.pathname === '/privacy-policy' ? activeLinkColor : linkColor}
              fontWeight={location.pathname === '/privacy-policy' ? 'semibold' : 'medium'}
              _hover={{ color: activeLinkColor, textDecoration: 'none' }}
              transition="color 0.2s"
              fontSize="sm"
              py={1}
            >
              Privacy Policy
            </Link>
            <Link
              as={RouterLink}
              to="/terms"
              color={location.pathname === '/terms' ? activeLinkColor : linkColor}
              fontWeight={location.pathname === '/terms' ? 'semibold' : 'medium'}
              _hover={{ color: activeLinkColor, textDecoration: 'none' }}
              transition="color 0.2s"
              fontSize="sm"
              py={1}
            >
              Terms & Conditions
            </Link>
            <Link
              as={RouterLink}
              to="/refund"
              color={location.pathname === '/refund' ? activeLinkColor : linkColor}
              fontWeight={location.pathname === '/refund' ? 'semibold' : 'medium'}
              _hover={{ color: activeLinkColor, textDecoration: 'none' }}
              transition="color 0.2s"
              fontSize="sm"
              py={1}
            >
              Refund Policy
            </Link>
          </HStack>

          {/* Copyright */}
          <Text color={footerText} fontSize="sm" textAlign="center">
            Copyright © {new Date().getFullYear()} Email Cleaner - All Rights Reserved.
          </Text>
        </VStack>
      </Container>
    </Box>
  );
};

// Main App Component
function App() {
  return (
    <Router>
      <ScrollToTop />
      <Layout>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/refund" element={<RefundPolicy />} />
          <Route path="/pricing" element={<PricingStatic />} />
          <Route path="/pricing-paddle" element={<Pricing />} />
          <Route path="/profile" element={<Profile />} />
        </Routes>
      </Layout>
    </Router>
  );
}

export default App;
